<script setup>
import { computed, watch } from 'vue';
import Modal from '../Modal.vue';
import PgeButton from '../../pge-components/pge-button.vue';
import useHubBrokers from '../../composables/useHubBrokers';
import { accessSummaryLine } from '../../composables/useResourceAccess';
import { plural } from '../../helpers/plural';

const props = defineProps({
  resources: { type: Array, required: true },
  access: { type: Object, required: true },
  members: { type: Array, default: () => [] },
  leftOut: { type: Number, default: 0 },
});

const emit = defineEmits(['cancel', 'confirm']);

const { loadHubBrokers } = useHubBrokers();
watch(() => props.access.broker_ids?.length, (count) => { if (count) loadHubBrokers(); }, { immediate: true });

const summary = computed(() => accessSummaryLine(props.access, props.members));

// Re-applying "everyone" to resources that are all already open changes
// nothing, and saying so beats showing a warning about an override that will
// not happen.
const isNoOp = computed(() =>
  props.access.all_participants
  && props.resources.every(resource => resource.access?.all_participants ?? true)
);

const leftOutLine = computed(() => `${plural(props.leftOut, 'file belongs', 'files belong')} to another broker's policyholders and ${props.leftOut === 1 ? 'stays as it is' : 'stay as they are'}.`);
</script>

<template>
  <modal
    title="Apply to all resources?"
    max-width="520px"
    content-with-border
    :on-close="() => emit('cancel')"
    @close="emit('cancel')"
  >
    <template #content>
      <p v-if="isNoOp" class="apply-all__note">
        All resources are already set to Everyone. No changes will be made.
      </p>
      <p v-else class="apply-all__warning">
        This will override the individual access settings for all
        {{ resources.length }}
        {{ resources.length === 1 ? 'resource' : 'resources' }} in this session.
      </p>

      <p v-if="leftOut > 0" class="apply-all__note" data-testid="apply-all-left-out">{{ leftOutLine }}</p>

      <p class="apply-all__summary">{{ summary }}</p>

      <ul class="apply-all__list">
        <li v-for="resource in resources" :key="resource.id ?? resource.name" class="truncate">
          {{ resource.name }}
        </li>
      </ul>
    </template>

    <template #actions>
      <pge-button template="outlined" label="Cancel" @click="emit('cancel')" />
      <pge-button label="Apply to all" @click="emit('confirm')" />
    </template>
  </modal>
</template>

<style lang="scss" scoped>
.apply-all__warning {
  padding: 12px;
  border-radius: 8px;
  background: #FDF3DC;
  color: #7A5B00;
  font-size: 13px;
}

.apply-all__note {
  padding: 12px;
  border-radius: 8px;
  background: #DCEBFB;
  color: #1B4F8A;
  font-size: 13px;
}

.apply-all__summary {
  font-weight: 600;
  color: #1D1B20;
}

.apply-all__list {
  margin: 0;
  padding-left: 20px;
  max-height: 220px;
  overflow-y: auto;

  li {
    font-size: 14px;
    line-height: 22px;
    color: #1D1B20;
  }
}
</style>
